import moment from 'moment'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'

import LoadingIndicator from '../../../components/ReusableComponents/LoadingIndicator'
import internalOnlyApi from '../../../services/api/internalOnly'
import styles from './DailyOperationsReport.module.scss'

const REPORT_RESPONSIVE_OVERRIDES = `
<style id="dashboard-report-responsive-overrides">
  body { overflow-wrap: anywhere; word-wrap: break-word; }
  .wrap { width: 100%; padding: 24px 16px 32px; }
  .head { flex-wrap: wrap; align-items: flex-start; }
  .head p { max-width: 640px; line-height: 1.45; }
  .stamp { white-space: normal; line-height: 1.5; }
  .kpis { grid-template-columns: repeat(auto-fit, minmax(min(100%, 148px), 1fr)) !important; }
  .kpi { min-width: 0; }
  .kpi-label { line-height: 1.35; }
  .columns { grid-template-columns: repeat(auto-fit, minmax(min(100%, 340px), 1fr)) !important; align-items: start; }
  .col, .item { min-width: 0; }
  .item-head { flex-wrap: wrap; }
  .item-head > div:first-child { min-width: 0; flex: 1 1 180px; }
  .hotel-id, .hotel-name, .problem, .action { overflow-wrap: anywhere; }
  .btn { min-height: 36px; padding: 8px 12px; display: inline-flex; align-items: center; justify-content: center; }
  @media (max-width: 720px) {
    .head { flex-direction: column; }
    .stamp { text-align: left; }
    .ctas { flex-direction: column; }
    .btn { width: 100%; }
  }
</style>
`

const withResponsiveReportStyles = htmlContent => {
  if (!htmlContent) {
    return htmlContent
  }

  if (htmlContent.includes('dashboard-report-responsive-overrides')) {
    return htmlContent
  }

  if (htmlContent.includes('</head>')) {
    return htmlContent.replace('</head>', `${REPORT_RESPONSIVE_OVERRIDES}</head>`)
  }

  return `${REPORT_RESPONSIVE_OVERRIDES}${htmlContent}`
}

const DailyOperationsReport = () => {
  const { date } = useParams()
  const [htmlContent, setHtmlContent] = useState('')
  const [isLiveSnapshot, setIsLiveSnapshot] = useState(false)
  const [isArchivedSnapshot, setIsArchivedSnapshot] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const iframeRef = useRef(null)

  const resizeReportFrame = useCallback(() => {
    const iframe = iframeRef.current
    const contentDocument = iframe?.contentDocument

    if (!iframe || !contentDocument) {
      return
    }

    const height = Math.max(
      contentDocument.body?.scrollHeight ?? 0,
      contentDocument.documentElement?.scrollHeight ?? 0
    )

    iframe.style.height = `${height}px`
  }, [])

  useEffect(() => {
    let cancelled = false

    const reportDateUtc = date
      ? moment.utc(date, 'YYYY-MM-DD', true).startOf('day').toISOString()
      : moment.utc().startOf('day').toISOString()

    setIsLoading(true)
    setError(null)

    internalOnlyApi
      .getPepDailyOperationsReport({ reportDateUtc })
      .then(payload => {
        if (cancelled) {
          return
        }

        if (!payload?.htmlContent && !payload?.HtmlContent) {
          throw new Error('Daily operations report HTML was empty.')
        }

        setHtmlContent(withResponsiveReportStyles(payload.htmlContent ?? payload.HtmlContent))
        setIsLiveSnapshot(Boolean(payload.isLiveSnapshot ?? payload.IsLiveSnapshot))
        setIsArchivedSnapshot(Boolean(payload.isArchivedSnapshot ?? payload.IsArchivedSnapshot))
      })
      .catch(fetchError => {
        if (cancelled) {
          return
        }
        setError(fetchError instanceof Error ? fetchError.message : String(fetchError))
        setHtmlContent('')
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [date])

  useEffect(() => {
    if (!htmlContent) {
      return undefined
    }

    const handleResize = () => resizeReportFrame()
    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [htmlContent, resizeReportFrame])

  if (isLoading) {
    return (
      <div className={styles.page}>
        <LoadingIndicator />
      </div>
    )
  }

  if (error) {
    return (
      <div className={styles.page}>
        <p className={styles.error}>{error}</p>
      </div>
    )
  }

  return (
    <div className={styles.page}>
      {isLiveSnapshot && (
        <p className={styles.banner}>Live preview — official archived report publishes daily at 13:00 UTC.</p>
      )}
      {isArchivedSnapshot && (
        <p className={styles.bannerArchived}>Archived snapshot — this report will not change.</p>
      )}
      <iframe
        ref={iframeRef}
        className={styles.reportFrame}
        title="Daily Operations Report"
        srcDoc={htmlContent}
        sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
        onLoad={resizeReportFrame}
      />
    </div>
  )
}

export default DailyOperationsReport
